import {
  PaginationResponse_QuoteResponse_,
  PromotionEntityType,
  QuoteDataResponse,
  QuoteResponse,
  RequestCommonParams,
  SelectOptionType,
  apiGetQuotePromotionAllReleatedDetailInformation,
  filterOutRequestExtraParameters,
  getQuotePaymentBillTypeMap,
  getServerDayjsDateKeepLocalTime,
  handleApiRequestSuccessData,
  handleRequestError,
} from '@jove-frontend/uikit';
import camelcaseKeys from 'camelcase-keys';
import dayjs from 'dayjs';
import { isObject } from 'lodash';
import qs from 'qs';
import { AsyncResult, api } from '.';

export const handleQuoteDetailInterfaceReturnDataInfo = (data) => {
  const {
    projectPeriodStart,
    projectPeriodEnd,
    companyIncorporationDate,
    rateAndTotal,
    businessCountry,
    birthDate,
    country,
    retroactiveDate,
    brokerPaymentBillType,
  } = data;
  // Business country：In special cases
  if (!businessCountry) data.businessCountry = country;
  // date
  if (projectPeriodStart) {
    data.projectPeriodStart = getServerDayjsDateKeepLocalTime(projectPeriodStart);
  }

  if (projectPeriodEnd) {
    data.projectPeriodEnd = new Date(projectPeriodEnd);
  }
  if (companyIncorporationDate) {
    data.companyIncorporationDate = new Date(companyIncorporationDate);
  }
  // birth date
  if (birthDate) {
    data.birthDate = new Date(birthDate);
  }
  if (retroactiveDate) {
    data.retroactiveDate = dayjs(retroactiveDate);
  }
  // Chubb: contract - estimated income
  if (!rateAndTotal) {
    data.rateAndTotal = {
      rate: 150,
      perType: 'perDay',
      perCount: 5,
      incomeType: 'rate',
      income: 0,
    };
  }

  data.haveBindPromotion = !!data.promotionId;
  data.brokerPaymentBillType = brokerPaymentBillType || getQuotePaymentBillTypeMap().jove.value;
  data.isQuoteBrokerPromotion = data.promotionInfo?.promotionType === PromotionEntityType.BROKER_PROMOTION;
  data.assumptionFields = isObject(data.assumptionFields) ? camelcaseKeys(data.assumptionFields) : {};
};

export const getAllQuotes = async (): AsyncResult<PaginationResponse_QuoteResponse_> => {
  try {
    const res = await api.get<PaginationResponse_QuoteResponse_>('/admin/quotes');

    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to fetch validations because: ${message}`);

    return [null, err];
  }
};

export const getAdminQuote = async (id: string): AsyncResult<PaginationResponse_QuoteResponse_> => {
  try {
    const res = await api.get<PaginationResponse_QuoteResponse_>(`/admin/quotes?offset=1&limit=1&id__in=${id}`);
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to fetch validations because: ${message}`);

    return [null, err];
  }
};

export const getQuote = async (id: string): AsyncResult<QuoteResponse> => {
  try {
    const res = await api.get<QuoteResponse>(`/quotes/${id}`);

    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to fetch validations because: ${message}`);

    return [null, err];
  }
};

// query questions list
// query questions list
export const apiQueryQuotezDetailQuestions = async (providerCode: string) => {
  try {
    const res = await api.get(`/admin/quotes/qsets/declaration-questions?provider_code=${providerCode}`);

    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to fetch quote questions because: ${message}`);

    return [null, err];
  }
};

// update quote info
export const apiUpdateQuoteInfo = async (
  payload: {
    quote_id: string;
    assigneeId?: string;
  } & QuoteDataResponse
): AsyncResult => {
  try {
    const res = await api.patch(`/admin/quotes/${payload?.quote_id}`, payload);
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to fetch validations because: ${message}`);

    return [null, err];
  }
};

// qoute detail: set a prospect
export const apiQuoteSetAProspect = async (data = {}) => {
  try {
    const res = await api.post(`/admin/quotes/qsets/declaration-questions`, data);
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;
    console.error(`Failed to fetch set a prospect because: ${message}`);
    return [null, err];
  }
};

// qoute detail: not interested quote
export const apiSubmitQuoteNotInterested = async (quoteId = '') => {
  try {
    const res = await api.get(`/admin/quotes/qsets/declaration-questions?id=${quoteId}`);
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;
    console.error(`Failed to fetch not interested because: ${message}`);
    return [null, err];
  }
};

// get quote detail info
export const apiGetQuoteDetailInfo = async (quoteId = '', config?: { requestPromotionInfo?: boolean }) => {
  try {
    const res = await api.get(`/admin/quotes/${quoteId}`);
    let promotionInfo = [];
    if (
      config?.requestPromotionInfo &&
      [PromotionEntityType.BROKER_PROMOTION, PromotionEntityType.PARTNER_PROMOTION].includes(res?.data?.promotion?.promotion_type)
    ) {
      promotionInfo = await apiGetQuotePromotionAllReleatedDetailInformation({ quoteId });
    }
    if (res) {
      let data = res.data || {};
      data = {
        ...(data.payload || {}),
        ...data,
        promotionInfo: !!promotionInfo?.[0]?.data?.promotionPayload
          ? {
              ...promotionInfo?.[0]?.data,
              payload: promotionInfo?.[0]?.data?.promotionPayload,
            }
          : promotionInfo?.[0]?.data,
      };
      handleQuoteDetailInterfaceReturnDataInfo(data);

      res.data = data;
    }
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;
    console.error(`Failed to fetch quote detail info because: ${message}`);
    return [null, err];
  }
};

// auto save quote info cache
export const apiAutoSaveQuoteInfo = async (quoteId: string, params: { responses: any; stages: Array<any> }) => {
  try {
    const res = await api.post(`/admin/quotes/${quoteId}/qsets/remaining`, params);
    // permium type
    if (res.data && res.data.responses) {
      handleQuoteDetailInterfaceReturnDataInfo(res.data.responses);
    }
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;
    console.error(`Failed to auto save quote info because: ${message}`);
    return [null, err];
  }
};

// get quote calculate price info
export const apiGetQuoteCalPriceInfo = async (payload: { quoteId: string }) => {
  try {
    const res = await api.post(`/admin/quotes/${payload.quoteId}/send`);
    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};

// save quote info cache
export const apiSaveQuoteInfo = async (quoteId: string, params: { responses: any; stages: Array<any> }) => {
  try {
    const res = await api.post(`/admin/quotes/${quoteId}/send-confirm`, params);
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;
    console.error(`Failed to save quote info because: ${message}`);
    return [null, err];
  }
};

// set a quote prospect
export const apiSetAQuoteProspect = async (params: {
  id: string;
  current_insurance_products: string;
  insurance_provider: string;
  renewal_date: string;
  monthly_premium: number;
  no_any_insurance: boolean;
}) => {
  try {
    const res = await api.post(`/prospect/create`, params);
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;
    console.error(`Failed to set a prospect because: ${message}`);
    return [null, err];
  }
};

// get quote calculate price info
export const apiGetQuoteCalculatePriceInfo = async (quoteId: string, params: { responses: any; stages: Array<any> }) => {
  try {
    const res = await api.post(`/admin/quotes/${quoteId}/calc-price`, params);
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;
    console.error(`Failed to calculate quote price info because: ${message}`);
    return [null, err];
  }
};

// get quote detail info v2
export const apiGetCmsQuoteDetailInfo = async (quoteId = '') => {
  try {
    const res = await api.get(`/admin/quotes/${quoteId}/v2`);
    if (res) {
      let data = res.data || {};
      data = { ...(data.payload || {}), ...data };

      handleQuoteDetailInterfaceReturnDataInfo(data);

      res.data = data;
    }
    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;
    console.error(`Failed to fetch quote detail info because: ${message}`);
  }
};

export const apiSendDropoutQuoteEmail = async (payload: { quoteId: string }) => {
  try {
    const res = await api.post<QuoteResponse>(`/admin/quotes/${payload.quoteId}/send-dropout-email`);

    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to send drop out quote email because: ${message}`);

    return [null, err];
  }
};

// Upload the signature documnet of quote: It is currently only used in great american.
export const apiUploadQuoteSignatureDocument = async (payload: { formData: FormData }) => {
  try {
    const res = await api.post(`/quote_documents`, payload.formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to upload quote signature document because: ${message}`);

    return [null, err];
  }
};

// Delete the signature documnet of quote: It is currently only used in great american.
export const apiDeleteQuoteSignatureDocument = async (payload: { quote_document_id: string; cancelled_at: string }) => {
  try {
    const res = await api.patch(`/quote_documents/${payload.quote_document_id}`, payload);

    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to Delete quote signature document because: ${message}`);

    return [null, err];
  }
};

// Query the total count of quote status
export const apiQueryQuoteStatusTotalCount = async (
  payload: {
    quote_viewed?: number;
    email_viewed?: number;
  } & RequestCommonParams,
  config?: any
) => {
  payload.hideErrToast = true;
  try {
    const res = await api.get(`/admin/quotes/statistics/status-counts?${qs.stringify(payload)}`, config);
    if (!res.data) res.data = [];

    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};

// Send payment email if payment failed etc.
export const apiDirectlySendPaymentEmail = async (payload: { quote_id: string; payment_intent_id: string; failed_date: string }) => {
  try {
    const res = await api.post(`/admin/quotes/${payload.quote_id}/send-missing-payment-email`, payload);

    return [res.data];
  } catch (err) {
    const message = err.response?.data?.detail?.[0]?.msg;

    console.error(`Failed to send payment email because: ${message}`);
    return [null, err];
  }
};

// Repair quote data
export const apiRepairQuoteData = async (
  payload: {
    quote_id: string;
    policy_number: string;
    company_name: string;
  } & RequestCommonParams
) => {
  try {
    const res = await api.patch(`/admin/quotes/${payload.quote_id}/revise-quote`, payload);

    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};

// Great American: Call Great American interface to get the price when quote is referral
export const apiCallGreatAmericanRefferProcess = async (
  payload: {
    quote_id: string;
  } & RequestCommonParams
) => {
  try {
    const res = await api.patch(`/great_american/${payload.quote_id}/approve-referral`);

    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};

// Great American: Decline when quote is referral
export const apiDeclineGreatAmericanRefferProcess = async (
  payload: {
    quote_id: string;
  } & RequestCommonParams
) => {
  try {
    const res = await api.patch(`/great_american/${payload.quote_id}/decline-referral`);

    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};

// Great American: Send referral email manually
export const apiSendReferralEmailToGreatAmerican = async (
  payload: {
    quote_id: string;
  } & RequestCommonParams
) => {
  try {
    const res = await api.post(`/admin/quotes/${payload.quote_id}/send_ga_referral_email_to_ga`);

    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};

// Quote: Marked quote tag
export const apiMarkedQuoteTagInfo = async (
  payload: {
    quote_id: string;
    tag: string;
    value?: number | boolean;
  } & RequestCommonParams
) => {
  try {
    const res = await api.patch(`/admin/quotes_plus/${payload.quote_id}/set-tag`, payload);

    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};

// Analytics filter options
export const fetchAnalyticsFilterOptions = async (): AsyncResult<{
  utm_source?: string[];
  utm_medium?: string[];
  utm_campaign?: string[];
}> => {
  try {
    const res = await api.get('/admin/quotes/analytics-filter-options');
    return [res.data];
  } catch (err) {
    return handleRequestError(err, { hideErrToast: true });
  }
};

// Helper function to transform analytics filter options into SelectOptionType format
export const transformAnalyticsFilterOptions = (options: string[] = [], allLabel: string = 'All'): SelectOptionType[] => {
  const transformedOptions = options.map((option) => ({
    value: option,
    label: option,
  }));

  return [{ value: '', label: allLabel }, ...transformedOptions];
};

// Quote: Duplicate quote
export const apiDuplicateQuote = async (
  payload: {
    quoteId: string;
  } & RequestCommonParams
) => {
  try {
    const res = await api.post(`/admin/quotes/${payload.quoteId}/copy`, payload);

    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};

// Query the total count of broker quote
export const apiQueryBrokerQuoteStatusTotalCount = async (payload?: { partner_id?: string } & RequestCommonParams, config?: any) => {
  try {
    const res = await api.get(`/admin/quotes/statistics/broker/status-counts?${qs.stringify(filterOutRequestExtraParameters(payload))}`, config);
    if (!res.data) res.data = [];

    return [res.data];
  } catch (err) {
    return handleRequestError(err, payload);
  }
};
